import React, { useState, useEffect } from "react";
import { addHardwareBackListener, exitNativeApp } from "@/lib/platform";
import { base44 } from "@/api/base44Client";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { useFeaturePermissions } from "@/lib/FeaturePermissionContext";
import AppHeader from "@/components/drill/AppHeader";
import { Button } from "@/components/ui/button";
import HoleForm from "@/components/drill/HoleForm";
import HomeBanner from "@/components/drill/HomeBanner";
import StocktakeSplash from "@/components/drill/StocktakeSplash";
import AdminUserManagementBanner from "@/components/drill/AdminUserManagementBanner";
import { mergeRecommendedItems } from "@/lib/orderStore";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { safeLocalStorage } from "@/lib/browserStorage";

export default function Home() {
  const { effectiveRole, user } = useAuth();
  const { canUseFeature } = useFeaturePermissions();
  const showReporting = effectiveRole === "supervisor" || effectiveRole === "admin" || effectiveRole === "owner";
  const [searchParams, setSearchParams] = useSearchParams();
  const [creating, setCreating] = useState(false);
  const dialogOpen = searchParams.has("newHole");
  const mode = searchParams.get("newHole") || "Diamond";
  useEffect(() => {
    if (!dialogOpen) return;
    const featureByType = { Diamond: "drilling_diamond", Conventional: "drilling_conventional", Chipping: "drilling_chipping", "Air Core": "drilling_air_core", RC: "drilling_rc" };
    if (!canUseFeature(featureByType[mode] || "drilling_diamond")) closeHoleDialog();
  }, [dialogOpen, mode, canUseFeature]);

  const openHoleDialog = (type) => {
    const next = new URLSearchParams(searchParams);
    next.set("newHole", type);
    setSearchParams(next);
  };

  const closeHoleDialog = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("newHole");
    setSearchParams(next);
  };
  const [stocktakeOpen, setStocktakeOpen] = useState(false);
  const [rigId, setRigId] = useState("");
  const [stocktakeItems, setStocktakeItems] = useState({});
  const [savedStocktakes, setSavedStocktakes] = useState([]);
  const [duplicateHole, setDuplicateHole] = useState(null);
  const [savedLoading, setSavedLoading] = useState(false);
  const [logo, setLogo] = useState(() => safeLocalStorage.getItem("drilllog_company_logo") || "");
  const [companyName, setCompanyName] = useState(() => safeLocalStorage.getItem("drilllog_company_name") || "");
  const navigate = useNavigate();

  const loadSavedStocktakes = async () => {
    setSavedLoading(true);
    try {
      const list = await base44.entities.Stocktake.list("-created_date", 50);
      setSavedStocktakes(list || []);
    } finally {
      setSavedLoading(false);
    }
  };

  useEffect(() => {
    loadSavedStocktakes();
  }, []);



  // Android Back should exit the app only when the user is already on Home.
  // Other app screens own their Back behaviour and must not be intercepted here.
  useEffect(() => {
    let listener;
    addHardwareBackListener(() => {
      if (stocktakeOpen) {
        setStocktakeOpen(false);
        return;
      }
      if (dialogOpen) {
        setSearchParams((current) => {
          const next = new URLSearchParams(current);
          next.delete("newHole");
          return next;
        });
        return;
      }
      void exitNativeApp();
    }).then((handle) => {
      listener = handle;
    });
    return () => {
      listener?.remove();
    };
  }, [stocktakeOpen, dialogOpen, setSearchParams]);

  const flattenStocktake = (obj) =>
    Object.entries(obj || {}).flatMap(([heading, list]) =>
      (list || []).map((entry) => ({ heading, ...entry }))
    );

  const groupStocktake = (arr) => {
    const out = {};
    (arr || []).forEach(({ heading, ...rest }) => {
      if (!heading) return;
      (out[heading] = out[heading] || []).push(rest);
    });
    return out;
  };

  const handleSaveStocktake = async (name) => {
    await base44.entities.Stocktake.create({
      name,
      rig_id: rigId,
      items: flattenStocktake(stocktakeItems),
    });
    loadSavedStocktakes();
  };

  const handleLoadSaved = (saved) => {
    setRigId(saved.rig_id || "");
    setStocktakeItems(groupStocktake(saved.items));
  };

  const handleDeleteSaved = async (id) => {
    await base44.entities.Stocktake.delete(id);
    loadSavedStocktakes();
  };

  const handleAddStocktakeItem = (heading, name, quantity, subs, bits, casing, buildTo, uom) => {
    setStocktakeItems((prev) => ({
      ...prev,
      [heading]: [...(prev[heading] || []), { name, quantity, subs, bits, casing, build_to: buildTo, uom }],
    }));
  };

  const handleAdjustStocktakeQuantity = (heading, index, delta) => {
    setStocktakeItems((prev) => {
      const list = prev[heading] || [];
      const next = [...list];
      const item = next[index];
      if (!item) return prev;
      const qty = Math.max(0, (item.quantity ?? 0) + delta);
      next[index] = { ...item, quantity: qty };
      return { ...prev, [heading]: next };
    });
  };

  const handleEditStocktakeItem = (heading, index, updates) => {
    setStocktakeItems((prev) => {
      const list = prev[heading] || [];
      const next = [...list];
      const item = next[index];
      if (!item) return prev;
      next[index] = { ...item, ...updates };
      return { ...prev, [heading]: next };
    });
  };

  const handleRemoveStocktakeItem = (heading, index) => {
    setStocktakeItems((prev) => {
      const list = prev[heading] || [];
      if (!list[index]) return prev;
      const next = list.filter((_, i) => i !== index);
      const out = { ...prev, [heading]: next };
      if (next.length === 0) delete out[heading];
      return out;
    });
  };

  const handleAddRecommendedToOrder = () => {
    const recommended = flattenStocktake(stocktakeItems)
      .map(({ heading, ...item }) => ({
        category: heading,
        name: item.name || "Item",
        uom: item.uom || "each",
        quantity: Math.max(0, Number(item.build_to || 0) - Number(item.quantity || 0)),
      }))
      .filter((item) => item.quantity > 0);
    if (!recommended.length) return;
    mergeRecommendedItems(recommended);
    setStocktakeOpen(false);
    navigate("/order");
  };

  const handleAddCategoryToOrder = (heading) => {
    const list = stocktakeItems[heading] || [];
    const rows = list.map((item) => ({ category: heading, name: item.name || "Item", uom: item.uom || "each", quantity: Math.max(0, Number(item.build_to || 0) - Number(item.quantity || 0)) })).filter((x)=>x.quantity>0);
    if (rows.length) mergeRecommendedItems(rows);
  };
  const handleAddItemToOrder = (heading, index) => {
    const item = (stocktakeItems[heading] || [])[index];
    if (!item) return;
    const qty = Math.max(1, Number(item.build_to || 0) - Number(item.quantity || 0));
    if (qty > 0) mergeRecommendedItems([{ category: heading, name: item.name || "Item", uom: item.uom || "each", quantity: qty }]);
  };

  const handleClearQuantities = () => {
    setStocktakeItems((prev) => {
      const out = {};
      Object.entries(prev).forEach(([heading, list]) => {
        out[heading] = list.map((item) => ({
          ...item,
          quantity: 0,
        }));
      });
      return out;
    });
  };

  const createHoleNow = async (payload) => {
    setCreating(true);
    try {
      const sheet = await base44.entities.Sheet.create(payload);
      navigate(`/run-sheet/${sheet.id}`);
    } finally {
      setCreating(false);
    }
  };

  const handleCreate = async (payload) => {
    const existing = await base44.entities.Sheet.filter({ name: payload.name });
    const activeDuplicate = (existing || []).find((s) => String(s.status || "Active") !== "Completed");
    if (activeDuplicate) {
      setDuplicateHole(payload);
      return;
    }
    await createHoleNow(payload);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      <AppHeader title="Drill Run Sheet" subtitle="Log and track drilling operations" />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 pb-36">
        {logo && (
          <div className="flex justify-center mb-6">
            <img src={logo} alt={companyName || "Company logo"} className="max-h-16 w-auto max-w-[70%] object-contain" />
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="sm:col-span-2">
            <HomeBanner
              title="Drilling Method"
              subtitle="Choose a drilling mode to start logging a new hole."
              actions={[
                canUseFeature("drilling_diamond") && { label: "Diamond", onClick: () => openHoleDialog("Diamond") },
                canUseFeature("drilling_chipping") && { label: "Chipping", onClick: () => openHoleDialog("Chipping") },
                canUseFeature("drilling_conventional") && { label: "Conventional", onClick: () => openHoleDialog("Conventional") },
                canUseFeature("drilling_air_core") && { label: "Air Core", onClick: () => openHoleDialog("Air Core") },
                canUseFeature("drilling_rc") && { label: "RC", onClick: () => openHoleDialog("RC") },
              ].filter(Boolean).sort((a,b) => a.label.localeCompare(b.label))}
            />
          </div>
          <div className="sm:col-span-2">
            <HomeBanner title="Notebook" subtitle="Field notebook tools." actions={[
              canUseFeature("daily_log") && { label: "Daily log", onClick: () => navigate("/daily-log") },
              canUseFeature("daily_report") && { label: "Daily Report", onClick: () => navigate("/daily-report") },
              canUseFeature("documents") && { label: "Documents", onClick: () => navigate("/documents") },
              canUseFeature("handover") && { label: "Handover", onClick: () => navigate("/handover") },
            ].filter(Boolean).sort((a,b) => a.label.localeCompare(b.label))} />

          </div>
          <div className="sm:col-span-2">
            <HomeBanner
              title="Utility"
              subtitle="Utility calculators for drilling field checks."
              actions={[
                canUseFeature("tools") && { label: "Tools", onClick: () => navigate("/tools") },
                canUseFeature("calculators") && { label: "Calculators", onClick: () => navigate("/tools/calculators") },
                canUseFeature("stocktake") && { label: "Stocktake", onClick: () => setStocktakeOpen(true) },
                canUseFeature("order") && { label: "Order", onClick: () => navigate("/order") },
              ].filter(Boolean).sort((a,b) => a.label.localeCompare(b.label))}
            />
          </div>
          {showReporting && canUseFeature("reporting") && (
            <div className="sm:col-span-2">
              <HomeBanner
                title="Reporting"
                subtitle="View reports for rigs assigned to you."
                actions={[
                  { label: "View Reports", onClick: () => navigate("/reporting") },
                  ...(effectiveRole === "admin" || effectiveRole === "owner"
                    ? [{ label: "Logs", onClick: () => navigate("/admin/logs") }]
                    : []),
                ].sort((a,b) => a.label.localeCompare(b.label))}
              />
            </div>
          )}
        </div>

        {(effectiveRole === "admin" || effectiveRole === "owner") && (
          <div className="sm:col-span-2 mt-6">
            <AdminUserManagementBanner />
          </div>
        )}

        <p className="text-center text-slate-400 text-sm mt-8 dark:text-slate-500">
          Use the <span className="font-medium">Menu</span> to open the Dashboard
          and browse your existing holes.
        </p>
        <div className="mt-6 border-t border-slate-200/80 pt-5 text-center dark:border-slate-800">
          <div className="text-[11px] text-slate-400 dark:text-slate-500">Drill Log Pro by 2DudesTech PTY LTD</div>
          <div className="mt-2 flex justify-center gap-3 text-[11px] text-slate-400 dark:text-slate-500">
            <button type="button" className="hover:underline" onClick={() => navigate('/legal#privacy')}>Privacy Policy</button>
            <span aria-hidden="true">·</span>
            <button type="button" className="hover:underline" onClick={() => navigate('/legal#terms')}>Terms of Service</button>
            <span aria-hidden="true">·</span>
            <button type="button" className="hover:underline" onClick={() => navigate('/tools/coming-soon-support')}>Support</button>
          </div>
        </div>
      </main>

      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) closeHoleDialog(); }}>
        <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Hole</DialogTitle>
          </DialogHeader>
          <HoleForm
            onSubmit={handleCreate}
            submitting={creating}
            submitLabel="Create & Open"
            holeType={mode}
            defaultRigId={user?.rig_id || user?.full_name || user?.name || ""}
            lockRigId={false}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={!!duplicateHole} onOpenChange={(open) => !open && setDuplicateHole(null)}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader><DialogTitle>Duplicate Hole ID</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600 dark:text-slate-300">This hole ID already exists, are you sure you want to create a duplicate</p>
          <div className="flex justify-end gap-2"><Button variant="outline" className="rounded-xl" onClick={() => setDuplicateHole(null)}>No</Button><Button className="rounded-xl" onClick={async()=>{const p=duplicateHole;setDuplicateHole(null);await createHoleNow(p)}}>Yes</Button></div>
        </DialogContent>
      </Dialog>

      <StocktakeSplash
        open={stocktakeOpen}
        onOpenChange={setStocktakeOpen}
        rigId={rigId}
        onRigIdChange={setRigId}
        items={stocktakeItems}
        onAddItem={handleAddStocktakeItem}
        onAdjustQuantity={handleAdjustStocktakeQuantity}
        onEditItem={handleEditStocktakeItem}
        onRemoveItem={handleRemoveStocktakeItem}
        onClearQuantities={handleClearQuantities}
        onSave={handleSaveStocktake}
        savedStocktakes={savedStocktakes}
        savedLoading={savedLoading}
        onLoadSaved={handleLoadSaved}
        onDeleteSaved={handleDeleteSaved}
        onAddRecommendedToOrder={handleAddRecommendedToOrder}
        onAddCategoryToOrder={handleAddCategoryToOrder}
        onAddItemToOrder={handleAddItemToOrder}
      />
    </div>
  );
}